import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './DonationForm.css';

import { FaDollarSign, FaAngleDown } from 'react-icons/fa';
import axios from "axios";
import OfflineInstructions from '../../../components/OfflineInstructions/OfflineInstructions';
import { donations, images } from '../../../constants';

// API endpoint for form submission
const API_PATH = 'https://siraj-react.asdesignsgalaxy.com/api/phpmailer/donation.php';

// Regular expressions for validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

// Transform donation options for dropdown
const donationOptions = donations.map(donation => ({
  value: donation.title.replace(/\s+/g, '_'),
  label: donation.title
}));

// Types of Islamic donations
const donationTypes = [
  { value: 'general', label: 'General Donation' },
  { value: 'zakat', label: 'Zakat' },
  { value: 'sadaqah', label: 'Sadaqah' },
  { value: 'lillah', label: 'Lillah' },
  { value: 'fidya', label: 'Fidya' },
  { value: 'kafarah', label: 'Kafarah' }
];

/**
 * DonationForm Component
 * Handles donation submissions with various donation types and payment methods
 */
const DonationForm = () => {

  // Router hooks for navigation and URL parameters
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const donationParam = searchParams.get('donation') || null; // Get donation type from URL if present

  // Form state management
  const [formData, setFormData] = useState({
    donationType: '',
    donationTitle: donationParam || '', // Pre-fill if URL parameter exists
    amount: '',
    payment: 'test', // Default to test payment
    name: '',
    email: '',
    number: '',
    address: '',
    comment: ''
  });

  // UI state management
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Handles input changes for form fields
   * @param {Object} e - The event object
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  /**
   * Handles preset amount selection
   * @param {number} amount - The selected donation amount
   */
  const handleAmountSelection = (amount) => {
    setSelectedAmount(amount);
    setFormData(prev => ({ ...prev, amount: amount.toString() }));
  };

  /**
   * Handles form submission
   * @param {Object} e - The form submission event
   */
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Form validation
    try {
      const validationErrors = [];
      
      if (!formData.donationType) validationErrors.push('Donation type is required');
      if (!formData.donationTitle) validationErrors.push('Donation title is required');
      if (!formData.amount) validationErrors.push('Donation amount is required');
      if (!formData.name.trim()) validationErrors.push('Name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.address.trim()) validationErrors.push('Address is required');

      // Throw validation errors if any exist
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Format current date for submission
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });
      postData.append('date', currentDate);

      // Submit to API
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle API response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      // Navigate to confirmation page with donation data
      navigate('/Donation-Confirmation', { 
        state: { 
          donationData: {
            ...formData,
            date: currentDate
          } 
        } 
      });

    } catch (err) {
      // Handle errors from API or validation
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      // Reset submission state
      setIsSubmitting(false);
    }
  };

  return (
    <section className="donation">
      <form className="donation-form form" onSubmit={handleSubmitForm}>
        <div className="donation-form-container">

          {/* Donation Type Field */}
          <div className="item personal-info">
            <h4>Donation Type:</h4>
            <div className="select">
              <div className="select-container">
                <select 
                  className="select-box" 
                  id="donationType" 
                  name="donationType"
                  value={formData.donationType}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select Type</option>
                  {donationTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <div className="icon-container">
                  <FaAngleDown />
                </div>
              </div>
            </div>
          </div>

          {/* Donation Title Field */}
          <div className="item personal-info">
            <h4>Donation Programs:</h4>
            
            {donationParam ? (
              // Display as read-only if donation type came from URL
              <div className="input-field">
                <input
                  type="text"
                  name="donationTitle"
                  className="box"
                  value={formData.donationTitle}
                  readOnly
                  disabled
                />
                <input type="hidden" name="donationTitle" value={donationParam} />
              </div>
            ) : (
              // Display as dropdown if no URL parameter
              <div className="select">
                <div className="select-container">
                  <select 
                    className="select-box" 
                    id="donationTitle" 
                    name="donationTitle"
                    value={formData.donationTitle}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select Donation</option>
                    {donationOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="icon-container">
                    <FaAngleDown />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Donation Amount Section */}
          <div className="item amount-box">
            <h4>Donation Amount:</h4>
            <div className="amount-input-box">
              <label htmlFor="amount" className="fas fa-dollar-sign"><FaDollarSign /></label>
              <input 
                type="number" 
                id="amount" 
                name="amount"
                placeholder="$10"
                value={formData.amount}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>
            {/* Preset amount buttons */}
            <div className="amount-choices">
              {[10, 30, 50, 75, 100].map((amount) => (
                <div 
                  key={amount}
                  className={`amount-item ${selectedAmount === amount ? 'selected' : ''}`}
                  onClick={() => handleAmountSelection(amount)}
                >
                  ${amount}.00
                </div>
              ))}
            </div>
          </div>

          {/* Payment Method Selection */}
          <div className="item">
            <h4>payment method:</h4>
            <div className="payment-options">
              <div className="payment">
                <input 
                  type="radio" 
                  id="test" 
                  name="payment" 
                  value="test"
                  checked={formData.payment === 'test'}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="test" className="radio-label">Test Donation</label>
              </div>
              <div className="payment">
                <input 
                  type="radio" 
                  id="offline" 
                  name="payment" 
                  value="offline"
                  checked={formData.payment === 'offline'}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
                <label htmlFor="offline" className="radio-label">Offline donation</label>
              </div>
            </div>
          </div>

          {/* Offline Donation Instructions (conditional render) */}
          {formData.payment === 'offline' && (
            <div className="offline-payment">
              <h4>Offline Donation Instructions:</h4>
              <OfflineInstructions />
            </div>
          )}

          {/* Personal Information Section */}
          <div className="item personal-info">
            <h4>personal information:</h4>
            <div className="input-box">
              <input 
                type="text" 
                className="box" 
                name="name" 
                id="name" 
                placeholder="Name" 
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
              <input 
                type="email" 
                className="box" 
                name="email" 
                id="email" 
                placeholder="Email" 
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>
            <div className="input-box">
              <input 
                type="tel" 
                className="box" 
                name="number" 
                id="number" 
                placeholder="Phone Number" 
                value={formData.number}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
              <input 
                type="text" 
                className="box" 
                name="address" 
                id="address" 
                placeholder="Address" 
                value={formData.address}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              />
            </div>
            <textarea 
              className="box" 
              rows="10" 
              cols="30" 
              name="comment" 
              id="comment" 
              placeholder="Your message" 
              value={formData.comment}
              onChange={handleInputChange}
              disabled={isSubmitting}
            ></textarea>
          </div>

          {/* Submit Button */}
          <button type="submit" className="btn" name="donate" id="donate" disabled={isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Donate Now'}
          </button>

          {/* Error Display */}
          {error && (
            <div className="error-message">
              {error.split(' | ').map((msg, i) => (
                <p key={i}>{msg}</p>
              ))}
            </div>
          )}
        </div>

        {/* Donation Image */}
        <div className='donation-image'>
          <img src={images.donationImage} alt='FAQs-Pic' />
        </div>

      </form>
    </section>
  );
};

export default DonationForm;